import { useEffect, useMemo, useState, useRef } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_TableOptions,
} from 'material-react-table';
import { Typography, TextField, Button, Box, IconButton, Checkbox, FormControl, InputLabel, MenuItem, Select, Pagination, PaginationItem, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { useSidebar } from "packages/novous_web/src/context/SideBarContext";
import { ArrowBack } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from 'packages/novous_web/src/iconComponent/infoIcon';
import AlertToast from 'packages/novous_web/src/common/components/Alert';
import useApiError from 'packages/novous_web/src/api/useApiError';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import TimeField from 'packages/novous_web/src/common/components/TimeField';
import UploadDeviceApk from './UploadDeviceApk';
import NotAuthorized from '../../../NotAuthorized';

interface DeviceLogs {
    deviceId: string;
    customerId: string;
    dataCreationTime: string;
    fileName: string;
    insertedBy: string;
    version: string;
    versionCode: number;
    responsePayload: Record<string, string>;
}

interface CompanyData {
    id: string;
    name: string;
    description: string;
    type: string;
    crn: string;
    address: {
        fullAddress: string;
        city: string;
        state: string;
        pincode: number;
        country: string;
    };
    contacts: {
        telephoneNo: string;
        mobileNo: string;
        email: string;
    };
    url: string;
    status: number;
    deviceTypes: string[] | null;
}

interface ApkData {
    apkFileName: string;
    versionCode: string;
    versionNumber: string;
    uploadDate: string;
}

interface DateFieldProps {
    label: string;
    name: string;
    value: string | null;
    onChange: (value: string | null) => void;
    isDisabled?: boolean;
}

const DateField: React.FC<DateFieldProps> = ({
    label,
    name,
    value,
    onChange,
    isDisabled = false,
}) => {
    return (
        <div className="flex flex-col w-full">
            <Typography variant="body2" fontWeight={600} color="textPrimary">
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    value={value ? dayjs(value, 'DD/MM/YYYY') : null}
                    onChange={(newValue) =>
                        onChange(newValue ? dayjs(newValue).format('DD/MM/YYYY') : null)
                    }
                    format="DD/MM/YYYY"
                    disabled={isDisabled}
                    slotProps={{
                        textField: { fullWidth: true, variant: 'outlined' },
                    }}
                    sx={(theme) => ({
                        width: '100%',
                        marginTop: '8px',
                        '& .MuiOutlinedInput-root': {
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: 400,
                            borderRadius: '8px',
                            borderWidth: '1px',
                            borderColor: theme.palette.action.selected,
                        },
                        '& .MuiInputBase-input': {
                            padding: '0px',
                            borderWidth: '0px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.main,
                        },
                    })}
                    className={`mt-2 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''
                        }`}
                />
            </LocalizationProvider>
        </div>
    );
};

export default function DeviceUpdates() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [driverDoc, setDriverDoc] = useState<any>();
    const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isSideBarOpen } = useSidebar();
    const isSmallScreen = useMediaQuery("(max-width:850px)");

    // Data states
    const [processedData, setProcessedData] = useState<DeviceLogs[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const paginatedArr = useRef<(string | null)[]>([null]);

    // Filter modal states
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [selectedApk, setSelectedApk] = useState<ApkData | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedDevice, setSelectedDevice] = useState<string>('');
    const [scheduleDateTime, setScheduleDateTime] = useState(
        new Date().toISOString().slice(0, 19).replace(/-/g, ":").replace("T", ":")
    );

    // API hooks
    const {
        data: DeviceLogsData,
        error: DeviceLogsError,
        errorStatus: DeviceLogsErrorStatus,
        loading: DeviceLogsLoading,
        fetchData: DeviceLogsRequestData,
    } = useAxios<any>(API_ENDPOINTS.users.DEVICE_OTA_APK_FETCH, "POST");

    const {
        data: DeviceApkLsitData,
        error: DeviceApkLsitError,
        errorStatus: DeviceApkLsitErrorStatus,
        loading: DeviceApkLsitLoading,
        fetchData: DeviceApkLsitRequestData,
    } = useAxios<any>(API_ENDPOINTS.users.DEVICE_OTA_APK_LIST, "GET");

    const isForbidden = DeviceLogsErrorStatus === '403' || DeviceApkLsitErrorStatus === '403';

    const {
        data: companyList,
        error: companyListError,
        loading: companyListLoading,
        fetchData: fetchCompanyListData,
    } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, "GET");

    const {
        data: companyListByCrn,
        error: companyListByCrnError,
        loading: companyListByCrnLoading,
        fetchData: fetchCompanyListByCrnData,
    } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, "POST");

    const {
        data: companyListByCrnDevices,
        error: companyListByCrnDevicesError,
        loading: companyListByCrnDevicesLoading,
        fetchData: fetchCompanyListByCrnDevicesData,
    } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_DEVICES}`, "POST");

    const {
        data: deviceLogRequest,
        error: deviceLogRequestError,
        loading: deviceLogRequestLoading,
        fetchData: fetchDeviceLogRequestData,
    } = useApiError<any>(`${API_ENDPOINTS.users.DEVICE_OTA_APK_SAVE}`, "POST");

    const {
        data: deviceReboot,
        error: deviceRebootError,
        loading: deviceRebootLoading,
        fetchData: fetchDeviceRebootData,
    } = useApiError<any>(`${API_ENDPOINTS.users.DEVICE_REBOOT}`, "POST");

    // const {
    //     data: deviceUpload,
    //     error: deviceUploadError,
    //     loading: deviceUploadLoading,
    //     fetchData: fetchDeviceUploadData,
    // } = useApiError<any>(`${API_ENDPOINTS.users.DEVICE_UPLOAD_APK}`, "POST");

    const [isRebootConfirmModalOpen, setIsRebootConfirmModalOpen] = useState(false);

    const [isUpgradeDeviceModalOpen, setIsUpgradeDeviceModalOpen] = useState(false);

    const [isUploadApkModalOpen, setIsUploadApkModalOpen] = useState(false);

    const confirmRebootDevices = async () => {
        if (!isAnyRowSelected) return;

        setIsSendingEmail(true);
        setIsRebootConfirmModalOpen(false);

        try {
            const selectedDevices = processedData.filter(row =>
                selectedRows[`${row.fileName}_${row.deviceId}`]
            );

            const rebootPromises = selectedDevices.map(device =>
                fetchDeviceRebootData({
                    customerId: device.customerId,
                    deviceId: device.deviceId
                })
            );

            const results = await Promise.all(rebootPromises);

            const allSuccess = results.every(result =>
                result.status === 200 || result.status === 201
            );

            if (allSuccess) {
                setAlertMessage("Reboot commands sent successfully to all selected devices");
                setAlertType("success");
            } else {
                const errorCount = results.filter(result =>
                    !(result.status === 200 || result.status === 201)
                ).length;
                setAlertMessage(`Failed to send reboot command to ${errorCount} device(s)`);
                setAlertType("error");
            }

            setAlertOpen(true);

            setCurrentPage(1);
            paginatedArr.current = [null];
            await DeviceLogsRequestData({
                fleetId: user.customer_id,
            });

        } catch (error) {
            console.error("Error rebooting devices:", error);
            setAlertMessage(
                (error as any)?.response?.message ||
                (error as Error)?.message ||
                "An error occurred while sending reboot commands"
            );
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setIsSendingEmail(false);
            setSelectedRows({});
            setIsLoading(false);
        }
    };

    const confirmUpgradeDevices = async () => {
        if (!isAnyRowSelected) return;

        setIsSendingEmail(true);
        setIsUpgradeDeviceModalOpen(false);

        try {
            const selectedDevices = processedData.filter(row =>
                selectedRows[`${row.fileName}_${row.deviceId}`]
            );

            console.log("selectedDevices", selectedDevices)

            const upgradeDevicePromises = selectedDevices.map(device => {
                const now = new Date();
                const currentDateTime = [
                    now.getFullYear(),
                    String(now.getMonth() + 1).padStart(2, '0'),
                    String(now.getDate()).padStart(2, '0'),
                    String(now.getHours()).padStart(2, '0'),
                    String(now.getMinutes()).padStart(2, '0'),
                    String(now.getSeconds()).padStart(2, '0')
                ].join(':');

                return fetchDeviceLogRequestData({
                    customerId: device.customerId,
                    deviceId: [device.deviceId],
                    fileName: device.fileName,
                    versionCode: device.versionCode,
                    version: device.version,
                    scheduledOtaTime: currentDateTime,
                });
            });

            const results = await Promise.all(upgradeDevicePromises);

            const allSuccess = results.every(result =>
                result.status === 200 || result.status === 201
            );

            if (allSuccess) {
                setAlertMessage("Reboot commands sent successfully to all selected devices");
                setAlertType("success");
            } else {
                const errorCount = results.filter(result =>
                    !(result.status === 200 || result.status === 201)
                ).length;
                setAlertMessage(`Failed to send reboot command to ${errorCount} device(s)`);
                setAlertType("error");
            }

            setAlertOpen(true);

            setCurrentPage(1);
            paginatedArr.current = [null];
            await DeviceLogsRequestData({
                fleetId: user.customer_id,
            });

        } catch (error) {
            console.error("Error rebooting devices:", error);
            setAlertMessage(
                (error as any)?.response?.message ||
                (error as Error)?.message ||
                "An error occurred while sending reboot commands"
            );
            setAlertType("error");
            setAlertOpen(true);
        } finally {
            setIsSendingEmail(false);
            setSelectedRows({});
            setIsLoading(false);
        }
    };

    // const handleUploadApk = async (formData: FormData) => {
    //     setIsSendingEmail(true);

    //     try {
    //         // Create a new FormData object to ensure proper formatting
    //         const payload = new FormData();

    //         // Append the file
    //         const file = formData.get('file') as File;
    //         payload.append('file', file);

    //         // Append the JSON data as a string
    //         payload.append('requestDto', JSON.stringify({
    //             fileName: formData.get('fileName') as string,
    //             versionNumber: formData.get('versionNumber') as string,
    //             versionCode: parseInt(formData.get('versionCode') as string),
    //             commitId: formData.get('commitId') as string,
    //             releaseNote: formData.get('releaseNote') as string
    //         }));

    //         const response = await fetchDeviceUploadData(payload);

    //         if (response.status === 200 || response.status === 201) {
    //             setAlertMessage("APK uploaded successfully");
    //             setAlertType("success");
    //             DeviceApkLsitRequestData();
    //         } else {
    //             setAlertMessage(response?.message || "Failed to upload APK");
    //             setAlertType("error");
    //         }
    //     } catch (error) {
    //         console.error("Error uploading APK:", error);
    //         setAlertMessage(
    //             (error as any)?.response?.message ||
    //             (error as Error)?.message ||
    //             "An error occurred while uploading the APK"
    //         );
    //         setAlertType("error");
    //     } finally {
    //         setIsSendingEmail(false);
    //         setAlertOpen(true);
    //         setIsUploadApkModalOpen(false);
    //     }
    // };

    const handleRebootDevices = () => {
        if (!isAnyRowSelected) return;
        setIsRebootConfirmModalOpen(true);
    };

    const handleUpgradeDevices = () => {
        if (!isAnyRowSelected) return;
        setIsUpgradeDeviceModalOpen(true);
    };

    const handleUloadDevices = () => {
        setIsUploadApkModalOpen(true);
    };

    // Initialize data
    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([
                    fetchCompanyListData(),
                    DeviceApkLsitRequestData(),
                    DeviceLogsRequestData({ fleetId: user.customer_id })
                ]);
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, []);


    // Process device logs data
    useEffect(() => {
        if (DeviceLogsData?.data?.deviceOtaLists) {
            setProcessedData(DeviceLogsData.data.deviceOtaLists.map((report: DeviceLogs) => ({
                ...report,
                totalEventsCount: (report.customerId ?? 0)
            })));
        }
    }, [DeviceLogsData]);

    // Handle APK file selection
    const handleApkFileNameChange = (value: string) => {
        const selectedApkFile = DeviceApkLsitData?.data?.find(
            (apk: ApkData) => apk.apkFileName === value
        );

        if (selectedApkFile) {
            setSelectedApk(selectedApkFile);
            // Reset company/user/device selections when APK changes
            setSelectedCompany(null);
            setSelectedUser(null);
            setSelectedDevice('');
        } else {
            setSelectedApk(null);
        }
    };

    // Handle company selection
    const handleCompanyChange = (value: string) => {
        const company = companyList?.data?.find((c: CompanyData) => c.name === value);
        setSelectedCompany(company || null);
        setSelectedUser(null);
        setSelectedDevice('');

        if (company) {
            fetchCompanyListByCrnData({
                userId: company.id,
                userCrn: company.crn
            });
        }
    };

    // Handle user selection
    const handleUserChange = (value: string) => {
        const user = companyListByCrn?.data?.find((u: any) => u.fullName === value);
        setSelectedUser(user || null);
        setSelectedDevice('');

        if (user) {
            fetchCompanyListByCrnDevicesData({
                fleetId: user.id,
            });
        }
    };

    // Handle device selection
    const handleDeviceChange = (value: string) => {
        setSelectedDevice(value);
    };

    // Handle select all rows
    const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const newSelectedRows: Record<string, boolean> = {};

        if (isChecked) {
            processedData.forEach(row => {
                newSelectedRows[`${row.fileName}_${row.deviceId}`] = true;
            });
        }

        setSelectedRows(newSelectedRows);
    };

    // Handle individual row selection
    const handleRowSelection = (fileName: string, deviceId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;

        setSelectedRows(prev => ({
            ...prev,
            [`${fileName}_${deviceId}`]: isChecked
        }));
    };

    // Check if all rows are selected
    const allRowsSelected = useMemo(() => {
        return Object.keys(selectedRows).length === processedData.length &&
            Object.keys(selectedRows).every(key => selectedRows[key]);
    }, [selectedRows, processedData]);

    // Check if some rows are selected (but not all)
    const someRowsSelected = processedData.some(row => selectedRows[row.deviceId]) && !allRowsSelected;

    // Check if any rows are selected
    const isAnyRowSelected = useMemo(() => {
        return Object.values(selectedRows).some(value => value);
    }, [selectedRows]);

    // Table columns
    const columns: MRT_ColumnDef<DeviceLogs>[] = useMemo(
        () => [
            {
                accessorKey: 'deviceId',
                header: 'Device ID',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'customerId',
                header: 'Customer ID',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'dataCreationTime',
                header: 'Date Of Creation',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => {
                    const dateTimeString = cell.getValue<string>();
                    const [year, month, day] = dateTimeString.split('-').slice(0, 3);
                    const formattedDate = new Date(`${year}-${month}-${day}`);
                    const options: Intl.DateTimeFormatOptions = {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    };
                    return (
                        <Typography variant="body2" fontWeight={400} noWrap>
                            {formattedDate.toLocaleDateString('en-GB', options)}
                        </Typography>
                    );
                },
            },
            {
                accessorKey: 'fileName',
                header: 'File Name',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => (
                    <Typography variant="body2" fontWeight={400} noWrap>
                        {cell.getValue<string | null>() || 'N/A'}
                    </Typography>
                ),
            },
            {
                accessorKey: 'insertedBy',
                header: 'Inserted By',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'version',
                header: 'Version',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'versionCode',
                header: 'Version Code',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => <Typography variant="body2" fontWeight={400} noWrap>{cell.getValue<string>()}</Typography>,
            },
            {
                accessorKey: 'responsePayload',
                header: 'Response Payload',
                enableColumnActions: false,
                size: isSmallScreen ? 90 : 150,
                muiTableBodyCellProps: { sx: { padding: '8px' } },
                Cell: ({ cell }) => {
                    const payload = cell.getValue<Record<string, string>>();
                    const hasResponse = Object.keys(payload || {}).length > 0;
                    return (
                        <Typography
                            variant="body2"
                            fontWeight={400}
                            noWrap
                            color={hasResponse ? "success.main" : "error.main"}
                        >
                            {hasResponse ? "Yes" : "No"}
                        </Typography>
                    );
                },
            },
            {
                accessorKey: 'actions',
                header: 'Select All',
                Header: ({ table }) => (
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2" fontWeight={700}>
                            Select All
                        </Typography>
                        <Checkbox
                            checked={allRowsSelected}
                            indeterminate={someRowsSelected}
                            onChange={handleSelectAllRows}
                        />
                    </Box>
                ),
                enableColumnActions: false,
                size: 150,
                muiTableBodyCellProps: {
                    sx: {
                        padding: '8px',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    },
                },
                Cell: ({ row }) => (
                    <Checkbox
                        checked={!!selectedRows[`${row.original.fileName}_${row.original.deviceId}`]}
                        onChange={handleRowSelection(row.original.fileName, row.original.deviceId)}
                        onClick={(e) => e.stopPropagation()}
                    />
                ),
            },
        ],
        [isSmallScreen, processedData, selectedRows, allRowsSelected, someRowsSelected]
    );

    // Table options
    const tableOptions: MRT_TableOptions<DeviceLogs> = {
        columns,
        data: processedData,
        enablePagination: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableGlobalFilter: false,
        enableDensityToggle: false,
        enableHiding: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableFullScreenToggle: false,
        enableColumnResizing: false,
        enableToolbarInternalActions: false,
        muiTableBodyProps: {
            sx: {
                padding: 0,
                margin: 0,
            },
        },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
                setSelectedRows(prev => ({
                    ...prev,
                    [`${row.original.fileName}_${row.original.deviceId}`]: !prev[`${row.original.fileName}_${row.original.deviceId}`]
                }));
            },
            sx: {
                cursor: 'pointer',
                backgroundColor: selectedRows[`${row.original.fileName}_${row.original.deviceId}`] ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
            },
        }),
    };

    // Initialize the table
    const table = useMaterialReactTable(tableOptions);

    const handleUploadModalClose = () => {
        setIsUploadApkModalOpen(false);
    }

    return (
        <>
            <AlertToast message={alertMessage} type={alertType} open={alertOpen} onClose={() => setAlertOpen(false)} />
            {isLoading ? <></> : isForbidden ? (
                <NotAuthorized />
            ) : (
                <div className='flex flex-col gap-4 w-full'>
                    {/* Back button */}
                    <div className="w-full">
                        <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
                            <ArrowBack className="mr-1" /> Back
                        </button>
                    </div>

                    {/* Header */}
                    <div className='w-full flex lg:flex-row md:flex-row flex-col justify-between gap-4 xs:flex-col items-start'>
                        <div className="max-w-max">
                            <div className="dashboard-header-left">
                                <div className="flex flex-col items-start">
                                    <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight='36px'>
                                        Device Updates
                                    </Typography>
                                    <Typography variant="subtitle1" fontSize={16} fontWeight={400} lineHeight='24px'>
                                        Select devices to perform any action
                                    </Typography>
                                </div>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-4 flex-wrap">
                            <CustomIconButton
                                text="Filter"
                                background='transparent'
                                outline={false}
                                onClick={() => setIsFilterModalOpen(true)}
                            />
                            <CustomIconButton
                                text="Reboot"
                                background='transparent'
                                color='error'
                                outline={false}
                                onClick={handleRebootDevices}
                                isDisabled={!isAnyRowSelected}
                            />
                            <CustomIconButton
                                text="Upload APK"
                                background="transparent"
                                variant='contained'
                                onClick={handleUloadDevices}
                            />
                            <CustomIconButton
                                text="Upgrade OTA"
                                variant='contained'
                                onClick={handleUpgradeDevices}
                                isDisabled={!isAnyRowSelected}
                            />
                        </div>
                    </div>

                    {/* Reboot Confirmation Modal */}
                    {isRebootConfirmModalOpen && (
                        <>
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999]"></div>
                            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 z-[1000] rounded-2xl shadow-xl flex flex-col w-full max-w-md mx-auto border border-gray-100">
                                {/* Header */}
                                <div className="flex justify-between items-start mb-4">
                                    <div className='flex flex-col gap-4'>
                                        <Typography variant="h5" className="font-bold text-gray-900">
                                            Reboot Devices
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-500 mt-1">
                                            Are you sure you want to reboot all selected devices? This action cannot be undone.
                                        </Typography>
                                    </div>
                                    <IconButton
                                        onClick={() => setIsRebootConfirmModalOpen(false)}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>

                                {/* Footer Buttons */}
                                <div className="flex justify-between gap-3 pt-4">
                                    <Button
                                        variant="outlined"
                                        onClick={() => setIsRebootConfirmModalOpen(false)}
                                        className="px-4 py-2 rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={confirmRebootDevices}
                                        disabled={isSendingEmail}
                                        className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm"
                                    >
                                        {isSendingEmail ? 'Rebooting...' : 'Reboot'}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Reboot Confirmation Modal */}
                    {isUpgradeDeviceModalOpen && (
                        <>
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999]"></div>
                            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 z-[1000] rounded-2xl shadow-xl flex flex-col w-full max-w-md mx-auto border border-gray-100">
                                {/* Header */}
                                <div className="flex justify-between items-start mb-4">
                                    <div className='flex flex-col gap-4'>
                                        <Typography variant="h5" className="font-bold text-gray-900">
                                            Device Ota upgradation
                                        </Typography>
                                        <Typography variant="body2" className="text-gray-500 mt-1">
                                            Are you sure you want to initiate OTA upgrades for all selected devices? This action will enable over-the-air updates for each device.
                                        </Typography>
                                    </div>
                                    <IconButton
                                        onClick={() => setIsUpgradeDeviceModalOpen(false)}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>

                                {/* Footer Buttons */}
                                <div className="flex justify-between gap-3 pt-4">
                                    <Button
                                        variant="outlined"
                                        onClick={() => setIsUpgradeDeviceModalOpen(false)}
                                        className="px-4 py-2 rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={confirmUpgradeDevices}
                                        disabled={isSendingEmail}
                                        className="px-4 py-2 rounded-lg bg-[#5547DD] hover:bg-[#5547DD] text-white shadow-sm"
                                    >
                                        {isSendingEmail ? 'Upgrading...' : 'Upgrade'}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}

                    <UploadDeviceApk
                        open={isUploadApkModalOpen}
                        handleClose={() => setIsUploadApkModalOpen(false)}
                    />

                    {/* Data Table */}
                    <MaterialReactTable table={table} />

                    {/* Pagination */}
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={DeviceLogsData?.data?.lastEvaluatedKey ? paginatedArr.current.length + 1 : paginatedArr.current.length}
                            page={currentPage}
                            onChange={(_, page) => {
                                if (page > currentPage && !DeviceLogsData?.data?.lastEvaluatedKey) return;
                                setCurrentPage(page);
                                DeviceLogsRequestData({
                                    fleetId: user.customer_id,
                                    lastEvaluatedKey: page === 1 ? null : paginatedArr.current[page - 1],
                                });
                            }}
                            shape="rounded"
                            color="primary"
                            siblingCount={0}
                            boundaryCount={0}
                            renderItem={(item) => {
                                if (item.type === "page" && item.page !== null && item.page > currentPage) {
                                    return null;
                                }
                                return <PaginationItem {...item} />;
                            }}
                        />
                    </Box>
                </div>
            )}

            {/* Filter Modal */}
            {isFilterModalOpen && (
                <>
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999]"></div>
                    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 z-[1000] rounded-2xl shadow-xl flex flex-col w-full max-w-[840px] mx-auto border border-gray-100">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <Typography variant="h5" className="font-bold text-gray-900">
                                    Filter Devices
                                </Typography>
                                <Typography variant="body2" className="text-gray-500 mt-1">
                                    Select APK and then filter by company, customer, and device.
                                </Typography>
                            </div>
                            <IconButton
                                onClick={() => {
                                    setIsFilterModalOpen(false);
                                    setSelectedApk(null);
                                    setSelectedCompany(null);
                                    setSelectedUser(null);
                                    setSelectedDevice('');
                                }}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* APK Selection */}
                        <div className="mb-4">
                            <Typography variant="subtitle1" className="font-semibold text-gray-700 mb-2">
                                Select APK File
                            </Typography>
                            <CustomDropDownInput
                                label="APK File"
                                options={DeviceApkLsitData?.data?.map((apk: ApkData) => ({
                                    value: apk.apkFileName,
                                    label: apk.apkFileName
                                })) || []}
                                value={selectedApk?.apkFileName || ''}
                                onChange={handleApkFileNameChange}
                                placeholder="Select APK File"
                                showDropdownArrow={true}
                            />
                        </div>

                        {/* Selected APK Details */}
                        {selectedApk && (
                            <div className="bg-gray-50 p-3 rounded-lg mb-4">
                                <Typography variant="subtitle2" className="font-medium text-gray-700 mb-2">
                                    Selected APK Details
                                </Typography>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    <div>
                                        <Typography variant="caption" className="text-gray-500">
                                            File Name
                                        </Typography>
                                        <Typography variant="body2" className='break-all'>{selectedApk.apkFileName}</Typography>
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500">
                                            Version Code
                                        </Typography>
                                        <Typography variant="body2" className='break-all'>{selectedApk.versionCode}</Typography>
                                    </div>
                                    <div>
                                        <Typography variant="caption" className="text-gray-500">
                                            Version Number
                                        </Typography>
                                        <Typography variant="body2" className='break-all'>{selectedApk.versionNumber}</Typography>
                                    </div>
                                    <div className='hidden'>
                                        <Typography variant="caption" className="text-gray-500">
                                            Upload Date
                                        </Typography>
                                        <Typography variant="body2" className='break-all'>
                                            {new Date(selectedApk.uploadDate).toLocaleDateString()}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Company/User/Device Selection */}
                        <div className={`mb-6 ${!selectedApk ? 'opacity-50 pointer-events-none' : ''}`}>
                            <Typography variant="subtitle1" className="font-semibold text-gray-700 mb-2">
                                Filter Devices
                            </Typography>
                            {!selectedApk && (
                                <div className="bg-blue-50 p-3 rounded-lg mb-3 flex items-center">
                                    <InfoIcon color="primary" fontSize="small" className="mr-2" />
                                    <Typography variant="body2" className="text-blue-700">
                                        Please select an APK file first to enable device filters
                                    </Typography>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {/* Company Select */}
                                <CustomDropDownInput
                                    label="Company"
                                    options={companyList?.data?.map((company: CompanyData) => ({
                                        value: company.name,
                                        label: company.name
                                    })) || []}
                                    value={selectedCompany?.name || ''}
                                    onChange={handleCompanyChange}
                                    placeholder="Select Company"
                                    isDisabled={!selectedApk}
                                    showDropdownArrow={true}
                                />

                                {/* User Select */}
                                <CustomDropDownInput
                                    label={selectedCompany ? "User" : "Select company first"}
                                    options={companyListByCrn?.data?.map((user: any) => ({
                                        value: user.fullName,
                                        label: user.fullName
                                    })) || []}
                                    value={selectedUser?.fullName || ''}
                                    onChange={handleUserChange}
                                    placeholder={selectedCompany ? "Select User" : "Select company first"}
                                    isDisabled={!selectedApk || !selectedCompany}
                                    showDropdownArrow={true}
                                />

                                {/* Device Select */}
                                <CustomDropDownInput
                                    label={selectedUser ? "Device" : "Select user first"}
                                    options={companyListByCrnDevices?.data?.map((device: any) => ({
                                        value: device.deviceId,
                                        label: device.deviceId
                                    })) || []}
                                    value={selectedDevice}
                                    onChange={handleDeviceChange}
                                    placeholder={selectedUser ? "Select Device" : "Select user first"}
                                    isDisabled={!selectedApk || !selectedUser}
                                    showDropdownArrow={true}
                                />
                            </div>
                        </div>

                        {/* <div className="flex gap-4 sm:flex-row flex-col">
                            <div className="flex-1">
                                <DateField
                                    label="Schedule Date"
                                    name="scheduleDate"
                                    value={scheduleDateTime ? dayjs(scheduleDateTime).format('DD/MM/YYYY') : null}
                                    onChange={(date) => {
                                        if (date) {
                                            const timePart = scheduleDateTime ? scheduleDateTime.split(':').slice(3).join(':') : '00:00:00';
                                            const newDateTime = `${date.split('/').reverse().join(':')}:${timePart}`;
                                            setScheduleDateTime(newDateTime);
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex-1">
                                <TimeField
                                    label="Schedule Time"
                                    name="scheduleTime"
                                    value={scheduleDateTime ? scheduleDateTime.split(':').slice(3).join(':') : '00:00'}
                                    onChange={(time) => {
                                        if (time) {
                                            const datePart = scheduleDateTime ? scheduleDateTime.split(':').slice(0, 3).join(':') : new Date().toISOString().slice(0, 10).replace(/-/g, ':');
                                            const newDateTime = `${datePart}:${time}`;
                                            setScheduleDateTime(newDateTime);
                                        }
                                    }}
                                />
                            </div>
                        </div> */}

                        {/* Schedule Time */}
                        <div className="mb-6">
                            <Typography variant="subtitle1" className="font-semibold text-gray-700 mb-2">
                                Schedule Date & Time
                            </Typography>
                            <TextField
                                // label="Schedule Date & Time"
                                type="datetime-local"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: new Date().toISOString().slice(0, 16),
                                }}
                                value={scheduleDateTime.slice(0, 16).replace(":", "-").replace(":", "-").replace(":", "T")}
                                onChange={(e) => {
                                    const selectedDateTime = new Date(e.target.value);
                                    const now = new Date();

                                    if (selectedDateTime < now) {
                                        const currentFormatted = new Date()
                                            .toISOString()
                                            .slice(0, 19)
                                            .replace(/-/g, ":")
                                            .replace("T", ":");
                                        setScheduleDateTime(currentFormatted);
                                    } else {
                                        const formattedDateTime = e.target.value
                                            .replace("T", ":")
                                            .replace(/-/g, ":");
                                        setScheduleDateTime(formattedDateTime + ":00");
                                    }
                                }}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        borderColor: '#e5e7eb',
                                    },
                                    '&:hover .MuiOutlinedInput-root': {
                                        borderColor: '#d1d5db',
                                    }
                                }}
                            />
                        </div>

                        {/* Footer Buttons */}
                        <div className="flex justify-between gap-3 pt-4 border-t border-gray-100">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setIsFilterModalOpen(false);
                                    setSelectedApk(null);
                                    setSelectedCompany(null);
                                    setSelectedUser(null);
                                    setSelectedDevice('');
                                }}
                                className="px-4 py-2 rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                disabled={!selectedDevice || !selectedApk}
                                onClick={async () => {
                                    setIsSendingEmail(true);
                                    if (selectedCompany && selectedDevice && selectedApk) {
                                        const requestData = {
                                            customerId: selectedCompany.crn,
                                            deviceId: [selectedDevice],
                                            fileName: selectedApk.apkFileName,
                                            versionCode: selectedApk.versionCode,
                                            version: selectedApk.versionNumber,
                                            scheduledOtaTime: scheduleDateTime,
                                            s3bucketUrl: `novus-adas-ota/${selectedApk.uploadDate}/${selectedApk.versionNumber}`
                                        }
                                        try {
                                            const response = await fetchDeviceLogRequestData(requestData);
                                            console.log('Full response:', response); // Debug log

                                            if (response.status === 201 || response.status === 200) {
                                                setAlertMessage(response?.message || "Operation successful");
                                                setAlertType("success");
                                            } else {
                                                setAlertMessage(
                                                    response?.error ||
                                                    response?.message ||
                                                    response.message ||
                                                    "Operation failed"
                                                );
                                                setAlertType("error");
                                            }

                                            setAlertOpen(true);
                                            setCurrentPage(1);
                                            paginatedArr.current = [null];
                                            setIsFilterModalOpen(false);
                                            setSelectedApk(null);
                                            setSelectedCompany(null);
                                            setSelectedUser(null);
                                            setSelectedDevice('');

                                            await DeviceLogsRequestData({
                                                fleetId: user.customer_id,
                                            });
                                        } catch (error) {
                                            console.error("Error:", error);
                                            setAlertMessage(
                                                (error as any)?.response?.message ||
                                                (error as Error)?.message ||
                                                "An unknown error occurred"
                                            );
                                            setAlertType("error");
                                            setAlertOpen(true);
                                        } finally {
                                            setIsSendingEmail(false);
                                            setIsLoading(false);
                                        }
                                    }
                                }}
                                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                            >
                                Apply Filters
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}